import React, { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { Background, ReactFlow, useReactFlow } from "@xyflow/react";

import "./DBSchema.css";
import "@xyflow/react/dist/style.css";

import DatabaseSchemaDemo from "./DBSchemaDashboard";
import Loader from "../../../../Layouts/Loader/Loader";
import { getRelativeTime } from "../../Utils/Helper";
import { useDBConnectorsContext } from "../../../../context/DBConnectorContext";
import MaterialIcon from "../../../../components/BuilderComponents/MaterialIcon";
import storageService from "../../../../services/storageService";

const nodeTypes = {
  databaseSchema: DatabaseSchemaDemo
};

const DBSchema = ({ onlyViews = false }) => {
  const { selectedConnector, isLoading, modifiedAt, getSchema, splitAndSetSchema, views, tables, edges } =
    useDBConnectorsContext();
  const [relativeTime, setRelativeTime] = useState(getRelativeTime(modifiedAt));
  const { setViewport, getViewport } = useReactFlow();

  const handleNodesChange = useCallback(
    (changes) => {
      const updatedTables = tables.map((node) => {
        const change = changes.find((c) => c.id === node.id);
        return change && change.type === "position"
          ? {
              ...node,
              position: {
                x: Math.round(change.position.x),
                y: Math.round(change.position.y)
              }
            }
          : node;
      });

      const updatedViews = views.map((node) => {
        const change = changes.find((c) => c.id === node.id);
        return change && change.type === "position"
          ? {
              ...node,
              position: {
                x: Math.round(change.position.x),
                y: Math.round(change.position.y)
              }
            }
          : node;
      });

      splitAndSetSchema({
        nodes: [...updatedTables, ...updatedViews],
        edges,
        modified_at: modifiedAt
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tables, views, edges, modifiedAt]
  );

  const handleZoom = (direction = "in") => {
    const { x, y, zoom } = getViewport();

    const zoomFactor = 1.2;
    const newZoom = direction === "in" ? Math.min(zoom * zoomFactor, 2) : Math.max(zoom / zoomFactor, 0.1);

    const deltaZoom = Math.abs(newZoom - zoom);

    // Adjust offset to simulate bottom-right focus
    const offsetX = 100 * deltaZoom;
    const offsetY = 100 * deltaZoom;

    const newViewport = {
      x: direction === "in" ? x - offsetX : x + offsetX,
      y: direction === "in" ? y - offsetY : y + offsetY,
      zoom: newZoom
    };

    setViewport(newViewport, { duration: 300 });
    saveViewport(newViewport, getViewport, selectedConnector.id, onlyViews);
  };

  const handleMoveUp = () => {
    const { x, y, zoom } = getViewport();
    const newViewport = { x, y: y - 50, zoom };
    setViewport(newViewport, { duration: 300 });
    saveViewport(newViewport, getViewport, selectedConnector.id, onlyViews);
  };

  const handleMoveDown = () => {
    const { x, y, zoom } = getViewport();
    const newViewport = { x, y: y + 50, zoom };
    setViewport(newViewport, { duration: 300 });
    saveViewport(newViewport, getViewport, selectedConnector.id, onlyViews);
  };

  useLayoutEffect(() => {
    const savedViewport = storageService.get(`reactFlowViewport_${selectedConnector.id}${onlyViews ? "_view" : ""}`);
    if (savedViewport) {
      const { x, y, zoom } = JSON.parse(savedViewport);
      setTimeout(() => setViewport({ x, y, zoom }, { duration: 0 }), 100);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConnector.id]);

  useEffect(() => {
    const updateRelativeTime = () => {
      setRelativeTime(getRelativeTime(modifiedAt));
    };

    updateRelativeTime();
    const interval = setInterval(updateRelativeTime, 60000);

    return () => clearInterval(interval);
  }, [modifiedAt]);

  useEffect(() => {
    getSchema(selectedConnector.id);

    //eslint-disable-next-line
  }, [selectedConnector.id]);

  let content;

  if ((!onlyViews && tables.length > 0) || (onlyViews && views.length > 0)) {
    content = (
      <>
        {isLoading && (
          <div className="overlay-loader">
            <Loader />
          </div>
        )}
        <div style={{ position: "absolute", right: "1rem", zIndex: "1", padding: "4px" }}>
          <p>Last Synced: {relativeTime}</p>
        </div>
        <div className="sm_blueprint_renderer_box diagramCanvasOutBox page_main_section">
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div className="sm_blueprint_functionboxOut no-select">
              <MaterialIcon name="zoom_in" title="zoom in" onClick={() => handleZoom()} />
              <hr></hr>
              <MaterialIcon name="zoom_out" title="zoom out" onClick={() => handleZoom("out")} />
              <hr></hr>
              <MaterialIcon name="arrow_upward" title="move up" onClick={handleMoveUp} />
              <hr></hr>
              <MaterialIcon name="arrow_downward" title="move down" onClick={handleMoveDown} />
            </div>
          </div>
          <div className="db_reactFlow">
            <ReactFlow
              elementsSelectable={false}
              onNodesChange={handleNodesChange}
              // zoomOnScroll={false}
              nodesConnectable={false}
              nodes={onlyViews ? views : tables}
              edges={edges}
              nodeTypes={nodeTypes}
              autoPanSpeed={2}
              translateExtent={[
                [0, 0],
                [Infinity, Infinity]
              ]}
              minZoom={0.1}
              maxZoom={2}
            >
              <Background style={{ zIndex: -1 }} />
            </ReactFlow>
          </div>
        </div>
      </>
    );
  } else if (!isLoading) {
    content = (
      <div style={{ height: "500px" }} className="d_flex w_100 justify_center align_center">
        <p className="font_size_medium">{`No ${onlyViews ? "Views" : "Tables"} found.`}</p>
      </div>
    );
  } else {
    content = (
      <div className="overlay-loader">
        <Loader />
      </div>
    );
  }

  return <div className="w_100 h_100">{content}</div>;
};

export default DBSchema;

const saveViewport = (viewport, getViewport, connectorId, onlyViews) => {
  const { x, y, zoom } = viewport || getViewport();
  storageService.set(`reactFlowViewport_${connectorId}${onlyViews ? "_view" : ""}`, JSON.stringify({ x, y, zoom }));
};
